import api from '@/services/api';
const ENTITY_ENDPOINT = 'material';

export class MaterialService {
  /**
   * Obtiene todos los materiales con filtros y paginación
   * @param {Object} params - Parámetros de filtrado y paginación
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findAll(params = {}) {
    return await api.get(`/${ENTITY_ENDPOINT}`, { params });
  }

  /**
   * Obtiene todos los materiales con filtros utilizando getParamsUrl
   * @param {string} queryString - String de parámetros generado por getParamsUrl
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findAllWithQuery(queryString) {
    return await api.get(`/${ENTITY_ENDPOINT}?${queryString}`);
  }

  /**
   * Busca materiales por descripción
   * @param {Object} params - Parámetros de búsqueda
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async search(params = {}) {
    return await api.get(`/${ENTITY_ENDPOINT}/search`, { params });
  }

  /**
   * Busca producto terminado por descripción comercial
   * @param {string} query - Término de búsqueda
   * @param {Object} additionalParams - Parámetros adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async searchProductoTerminado(query, additionalParams = {}) {
    return await api.get(`/${ENTITY_ENDPOINT}`, {
      params: {
        DescripcionComercial: query,
        ...additionalParams,
      },
    });
  }

  /**
   * Obtiene un material por su código
   * @param {string|number} codigo - Código del material
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findByCode(codigo) {
    return await api.get(`/${ENTITY_ENDPOINT}/${codigo}`);
  }

  /**
   * Crea un nuevo material
   * @param {Object} material - Datos del material a crear
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async create(material) {
    return await api.post(`/${ENTITY_ENDPOINT}`, material);
  }

  /**
   * Actualiza un material existente
   * @param {string|number} codigo - Código del material
   * @param {Object} material - Datos del material a actualizar
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async update(codigo, material) {
    return await api.patch(`/${ENTITY_ENDPOINT}/${codigo}`, material);
  }

  /**
   * Obtiene los campos que admite un tipo de ítem (diccionario de atributos con su etiqueta).
   * @param {string|number} codigo - Código del material tipo
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findCamposDisponibles(codigo) {
    return await api.get(`/${ENTITY_ENDPOINT}/campos-disponibles/${codigo}`);
  }

  /**
   * Actualiza los costos de múltiples materiales
   * @param {Array} costos - Array de costos a actualizar
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async updateCosts(costos) {
    return await api.patch(`/${ENTITY_ENDPOINT}/materiales-costos`, { costos });
  }

  /**
   * Elimina un material
   * @param {string|number} codigo - Código del material
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async delete(codigo) {
    return await api.delete(`/${ENTITY_ENDPOINT}/${codigo}`);
  }

  /**
   * Obtiene materiales por familia
   * @param {Object} filters - Filtros para la búsqueda
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findByFamily(filters = {}) {
    return await api.get(`/${ENTITY_ENDPOINT}`, { params: filters });
  }

  /**
   * Obtiene materiales por tipo
   * @param {number} tipo - Tipo de material
   * @param {number} page - Página
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findByType(tipo, page = 1) {
    return await api.get(`/${ENTITY_ENDPOINT}/`, {
      params: {
        page: +page,
        tipo,
      },
    });
  }

  /**
   * Obtiene materiales activos con paginación
   * @param {Object} filters - Filtros adicionales
   * @param {number} page - Página
   * @param {number} limit - Límite de elementos por página
   * @returns {Promise<Object>} Respuesta de la API
   */
  static async findActiveMaterials(filters = {}, page = 1, limit = 35) {
    return await api.get(`/${ENTITY_ENDPOINT}`, {
      params: {
        Activo: true,
        page,
        limit,
        ...filters,
      },
    });
  }
}

export default MaterialService;
